import dayjs from "dayjs";

import { SWReactCountryIcons, SWReactIcons } from "@similarweb/icons";
import { tableSimpleSort, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Favicon, Tooltip } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { TableRowDropdown } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/TableRowDropdown/TableRowDropdown";
import type {
    DropdownOnClickProps,
    IAiTrackerTableRow,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { BaseFlex, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { BrandsIndicatorComponent } from "../../../shared/components/brands-indicator/BrandsIndicatorComponent";

import { FlexTextEllipsis } from "./LLMTable.styled";
import type { LLMTableConfig, SortDirection } from "./types";
import { getRowTags } from "./utils";

export const getColumnConfig = (
    tableRowCount: number,
    onDropdownClick: (props: DropdownOnClickProps, row: IAiTrackerTableRow) => void,
    sorting: { dataIndex: keyof Array<IAiTrackerTableRow>[number]; sortDirection: SortDirection },
    isSubscribePending?: boolean,
): LLMTableConfig =>
    [
        {
            dataIndex: "name",
            title: translate("llm.homepage.table.column.campaign_name", {
                amount: tableRowCount,
            }),
            sortOrder: sorting.dataIndex === "name" ? sorting.sortDirection : undefined,
            sorter: tableSimpleSort("name"),
            width: "32%",
            render: (value: IAiTrackerTableRow["name"], row: IAiTrackerTableRow) => (
                <FlexRow
                    gap="8px"
                    alignItems="center"
                    width="100%"
                >
                    <Favicon src={row.primaryDomain?.favicon} />
                    <FlexTextEllipsis>{value}</FlexTextEllipsis>
                    {getRowTags(row)}
                    {row.isLocked ? (
                        <SWReactIcons
                            size="xs"
                            iconName="spark"
                        />
                    ) : null}
                </FlexRow>
            ),
        },
        {
            dataIndex: "brands",
            title: translate("llm.homepage.table.column.brand_name"),
            sortOrder: sorting.dataIndex === "brands" ? sorting.sortDirection : undefined,
            sorter: tableSimpleSort("brands"),
            render: (brands: string[]) => {
                if (!brands?.length) {
                    return NO_DATA_SIGN;
                }
                return (
                    <BrandsIndicatorComponent brands={brands}>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {brands[0]}
                        </Text>
                    </BrandsIndicatorComponent>
                );
            },
            width: "15%",
        },
        {
            dataIndex: "topicCount",
            title: translate("llm.homepage.table.column.topics"),
            sortOrder: sorting.dataIndex === "topicCount" ? sorting.sortDirection : undefined,
            sorter: (a: IAiTrackerTableRow, b: IAiTrackerTableRow) => (a?.topicCount ?? 0) - (b?.topicCount ?? 0),
            render: (topicCount: number = 0) => topicCount,
            width: "8%",
        },
        {
            dataIndex: "countryCode",
            title: translate("llm.homepage.table.column.location"),
            sortOrder: sorting.dataIndex === "countryCode" ? sorting.sortDirection : undefined,
            sorter: (a: IAiTrackerTableRow, b: IAiTrackerTableRow) => (a?.countryCode ?? 0) - (b?.countryCode ?? 0),
            render: (countryCode: number, row: IAiTrackerTableRow) => (
                <FlexRow
                    gap="8px"
                    alignItems="center"
                >
                    <SWReactCountryIcons
                        countryCode={countryCode}
                        size="xs"
                    />
                    {row.languageName ? (
                        <Tooltip content={row.languageName}>
                            <FlexTextEllipsis>{row.languageName}</FlexTextEllipsis>
                        </Tooltip>
                    ) : null}
                </FlexRow>
            ),
            width: 190,
        },
        {
            dataIndex: "ownerUserName",
            title: translate("llm.homepage.table.column.owner"),
            sortOrder: sorting.dataIndex === "ownerUserName" ? sorting.sortDirection : undefined,
            sorter: tableSimpleSort("ownerUserName"),
            render: (owner: string, row: IAiTrackerTableRow) => {
                if (row.isDemo) {
                    return translate("llm.homepage.table.column.owner.demo");
                } else if (!row.isOwner) {
                    return owner ?? translate("llm.homepage.table.column.owner.not_available");
                } else {
                    return translate("llm.homepage.table.column.owner.me");
                }
            },
        },
        {
            dataIndex: "createdDate",
            title: translate("llm.homepage.table.column.date"),
            sortOrder: sorting.dataIndex === "createdDate" ? sorting.sortDirection : undefined,
            sorter: (a: IAiTrackerTableRow, b: IAiTrackerTableRow) => dayjs(a.createdDate).diff(dayjs(b.createdDate)),
            render: (date: string) => dayjs(date).format(shortMonthDayAndYear),
        },
        {
            dataIndex: null,
            render: (_val, row: IAiTrackerTableRow) => {
                return (
                    <BaseFlex
                        flexDirection="row-reverse"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div>
                            <TableRowDropdown
                                row={row}
                                onClick={onDropdownClick}
                                isSubscribePending={isSubscribePending}
                            />
                        </div>
                    </BaseFlex>
                );
            },
            width: 110,
        },
    ] as LLMTableConfig;
